import { DnsOutlined, Inventory2Outlined, LinkOutlined, PlaceOutlined, SensorsOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { COLORS } from "assets";
import { FormRowSwitch, MyModal, SiteSelect, TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";

const defaultValue = {
  deviceId: "",
  siteId: "",
  name: "",
  frameServer: "",
  healthUrl: ""
};
const defaultValues = {
  ...defaultValue
};

const initState =
  process.env.NODE_ENV === "development"
    ? ({
        ...defaultValues,
        deviceId: "A2:4D:23:56:67",
        name: "AWS Instance"
      } as typeof defaultValues)
    : defaultValues;

interface IAdd {
  onSuccess?: () => void;
  onClose: () => void;
}

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

export default function AddBox({ onSuccess, onClose }: IAdd) {
  const [state, setState] = useState({ ...initState, recordVideo: false });
  const [errors, setErrors] = useState(defaultValue);

  const t = useTranslation();
  const disabledSubmit = useMemo(() => !!(errors.deviceId || !state.deviceId || !state.siteId), [state, errors]);
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("POST");
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.boxes, {
        body: JSON.stringify(state)
      }).catch(console.error);
    },
    [state, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess && onSuccess();
      onClose();
    }
  }, [response, enqueueSnackbar, onClose, onSuccess]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Add Surveillance Box"]}
      subtitle={t["Add Surveillance Box description"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            value={state.deviceId}
            label={t["Device ID"]}
            type="text"
            placeholder={t["e.g A2:4D:23:56:67"]}
            error={!!errors.deviceId}
            helperText={errors.deviceId || t["Device ID helper"]}
            startAdornment={<SensorsOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, deviceId: value }));
              setErrors((prevState) => ({ ...prevState, deviceId: "" }));
            }}
            fullWidth
            required
            autoFocus
          />
          <TextInput
            value={state.name}
            label={t["Box Name"]}
            type="text"
            placeholder={t["e.g Aeyron Office NSTP"]}
            error={!!errors.name}
            helperText={errors.name}
            startAdornment={<Inventory2Outlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, name: value }));
              setErrors((prevState) => ({ ...prevState, name: "" }));
            }}
            fullWidth
            required
          />
          <Box>
            <SiteSelect
              value={state.siteId}
              startAdornment={<PlaceOutlined sx={iconSx} />}
              onChange={(siteId) => setState((prevState) => ({ ...prevState, siteId }))}
            />
            <Box sx={{ mt: 0.75, px: 0.5 }}>
              <Typography variant="caption" sx={{ color: COLORS.surface.secondaryText, lineHeight: 1.5 }}>
                {t["Site helper"]}
              </Typography>
            </Box>
          </Box>
          <TextInput
            value={state.frameServer}
            label={t["Frame Server"]}
            type="text"
            placeholder={t["e.g server1"]}
            error={!!errors.frameServer}
            helperText={errors.frameServer || t["Frame Server helper"]}
            startAdornment={<DnsOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, frameServer: value }));
              setErrors((prevState) => ({ ...prevState, frameServer: "" }));
            }}
            fullWidth
          />
          <TextInput
            value={state.healthUrl}
            label={t["Health URL"] || "Health URL"}
            type="url"
            placeholder="https://example.com/"
            error={!!errors.healthUrl}
            helperText={
              errors.healthUrl ||
              t["Health URL helper"] ||
              "Public HTTPS tunnel URL used to show Tunnel Healthy/Down"
            }
            startAdornment={<LinkOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, healthUrl: value }));
              setErrors((prevState) => ({ ...prevState, healthUrl: "" }));
            }}
            fullWidth
          />
          <FormRowSwitch
            label={t["Record Video"]}
            description={t["Record Video helper"]}
            checked={state.recordVideo}
            onChange={(checked) => setState((prevState) => ({ ...prevState, recordVideo: checked }))}
          />
        </Stack>

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={disabledSubmit}
            endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
            sx={{
              minWidth: 140,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              color: COLORS.white,
              backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
              boxShadow: "none",
              "&:hover": {
                backgroundImage: `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`,
                filter: "brightness(1.05)",
                boxShadow: "none"
              }
            }}
          >
            {t["Submit"]}
          </Button>
        </Box>
      </Box>
    </MyModal>
  );
}
